import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@phosphor-icons/react';

import { useAdminTeamCredits } from '@/api-models/admin/credits';
import { useAdminTeam } from '@/api-models/admin/teams';
import { tierLabel } from '@/api-models/balance';
import { formatDate } from '@/features/admin/format';
import { CreditsCard } from '@/features/admin/components/CreditsCard';
import { LicenseCard } from '@/features/admin/components/LicenseCard';
import { SpinnerBlock } from '@/ui/Spinner';
import { Badge, type BadgeProps } from '@/ui/Badge';
import type { TeamRole } from '@/api-models/teams';
import { TEAM_ROLE_LABEL } from '@/features/teams/components/teamVisuals';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/Card';

const ROLE_VARIANT: Record<TeamRole, BadgeProps['variant']> = {
  owner: 'warning',
  admin: 'brand',
  member: 'neutral',
};

export function AdminTeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const { team, mutate: mutateTeam, isLoading } = useAdminTeam(teamId);
  const { credits, mutate: mutateCredits } = useAdminTeamCredits(teamId);

  async function refresh() {
    await Promise.all([mutateTeam(), mutateCredits()]);
  }

  if (isLoading) {
    return <SpinnerBlock message="Loading team…" />;
  }

  if (!team) {
    return (
      <>
        <Link
          to="/admin/teams"
          className="inline-flex items-center gap-1 text-2xs text-fg-muted no-underline hover:text-fg-secondary"
        >
          <ArrowLeftIcon size={12} />
          Back to teams
        </Link>
        <p className="text-xs text-fg-muted">Team not found.</p>
      </>
    );
  }

  return (
    <>
      <div>
        <Link
          to="/admin/teams"
          className="mb-3 inline-flex items-center gap-1 text-2xs text-fg-muted no-underline hover:text-fg-secondary"
        >
          <ArrowLeftIcon size={12} />
          Back to teams
        </Link>
        <header>
          <div className="flex items-center gap-2">
            <h1 className="page-title">{team.name}</h1>
            <Badge size="md">{tierLabel(team.license_tier)}</Badge>
            {team.is_exhausted && (
              <Badge size="md" variant="danger">
                Out of credit
              </Badge>
            )}
            {team.is_personal && <Badge size="md">Personal</Badge>}
          </div>
          <p className="page-subtitle">
            {team.slug} · created {formatDate(team.created_at)}
          </p>
        </header>
      </div>

      {teamId && <LicenseCard teamId={teamId} balance={team} onSaved={refresh} />}
      {teamId && credits && <CreditsCard teamId={teamId} credits={credits} onChanged={refresh} />}

      <Card padding="none" radius="2xl" className="overflow-hidden">
        <CardHeader>
          <CardTitle>
            Members <span className="font-normal text-fg-muted">({team.members.length})</span>
          </CardTitle>
        </CardHeader>
        {team.members.length === 0 ? (
          <CardContent className="text-xs text-fg-muted">No members.</CardContent>
        ) : (
          <div>
            {team.members.map((m) => (
              <div
                key={m.user_id}
                className="flex items-center justify-between border-b border-line px-5 py-3 last:border-b-0"
              >
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-fg">{m.name || m.email}</p>
                  <p className="truncate text-2xs text-fg-muted">{m.email}</p>
                </div>
                <div className="ml-3 flex items-center gap-3">
                  <Badge variant={ROLE_VARIANT[m.role]}>{TEAM_ROLE_LABEL[m.role]}</Badge>
                  <span className="whitespace-nowrap text-2xs text-fg-muted tabular-nums">
                    joined {formatDate(m.joined_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card padding="none" radius="2xl" className="overflow-hidden">
        <CardHeader>
          <CardTitle>
            Repositories <span className="font-normal text-fg-muted">({team.repositories.length})</span>
          </CardTitle>
        </CardHeader>
        {team.repositories.length === 0 ? (
          <CardContent className="text-xs text-fg-muted">No repositories.</CardContent>
        ) : (
          <div>
            {team.repositories.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between border-b border-line px-5 py-3 last:border-b-0"
              >
                <p className="truncate text-xs font-medium text-fg">{p.name}</p>
                <span className="whitespace-nowrap text-2xs text-fg-muted tabular-nums">
                  created {formatDate(p.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
